import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress,
  Alert, Chip, Tooltip, TextField, MenuItem, Grid2 as Grid, Collapse,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Inbox as InboxIcon, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { leadsSiteService, SiteLead } from '../../services/leads-site.service';

const TIPOS: Record<string, { label: string; color: any }> = {
  contacto: { label: 'Contacto', color: 'default' },
  governo: { label: 'Governo', color: 'info' },
  ong: { label: 'ONG', color: 'secondary' },
  developer: { label: 'Programador', color: 'warning' },
  outro: { label: 'Outro', color: 'default' },
};

const ESTADOS: SiteLead['estado'][] = ['novo', 'contactado', 'fechado'];
const ESTADO_COLOR: Record<string, any> = { novo: 'error', contactado: 'warning', fechado: 'success' };

const LeadsSite: React.FC = () => {
  const [leads, setLeads] = useState<SiteLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await leadsSiteService.list({
        tipo: filtroTipo || undefined,
        estado: filtroEstado || undefined,
      });
      setLeads(res.data.data || []);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  }, [filtroTipo, filtroEstado]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const mudarEstado = async (lead: SiteLead, estado: SiteLead['estado']) => {
    try {
      await leadsSiteService.setEstado(lead.id, estado);
      setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, estado } : l)));
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erro ao atualizar estado');
    }
  };

  const novos = leads.filter((l) => l.estado === 'novo').length;

  return (
    <Box p={3}>
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h4" fontWeight="bold">Leads do Site</Typography>
          <Typography variant="body2" color="text.secondary">
            Contactos dos formulários do site (Contacto, Governos, ONGs, Programadores)
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData}>Atualizar</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary">Total</Typography>
            <Typography variant="h4" fontWeight="bold">{leads.length}</Typography>
          </CardContent></Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card><CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary">Novos</Typography>
            <Typography variant="h4" fontWeight="bold" color="error.main">{novos}</Typography>
          </CardContent></Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Tipo" value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(TIPOS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Estado" value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Todos</MenuItem>
            {ESTADOS.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
          </TextField>
        </CardContent>
      </Card>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Tipo</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Origem</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Estado</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress /></TableCell></TableRow>
              ) : leads.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <InboxIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">Ainda não há leads.</Typography>
                </TableCell></TableRow>
              ) : (
                leads.map((lead) => (
                  <React.Fragment key={lead.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton size="small" onClick={() => setExpanded(expanded === lead.id ? null : lead.id)}>
                          {expanded === lead.id ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={TIPOS[lead.tipo]?.label || lead.tipo} color={TIPOS[lead.tipo]?.color || 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{lead.nome || '—'}</Typography>
                        {lead.organizacao && <Typography variant="caption" color="text.secondary">{lead.organizacao}</Typography>}
                      </TableCell>
                      <TableCell>
                        {lead.email && <div><a href={`mailto:${lead.email}`}>{lead.email}</a></div>}
                        {lead.telefone && <Typography variant="caption" color="text.secondary">{lead.telefone}</Typography>}
                      </TableCell>
                      <TableCell><Typography variant="caption">{lead.origem || '—'}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{new Date(lead.created_at).toLocaleString('pt-PT')}</Typography></TableCell>
                      <TableCell>
                        <TextField select size="small" value={lead.estado}
                          onChange={(e) => mudarEstado(lead, e.target.value as SiteLead['estado'])}
                          variant="standard" sx={{ minWidth: 110 }}
                          SelectProps={{ renderValue: (v: any) => <Chip size="small" label={v} color={ESTADO_COLOR[v]} /> }}>
                          {ESTADOS.map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                        </TextField>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                        <Collapse in={expanded === lead.id} unmountOnExit>
                          <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2, my: 1 }}>
                            {lead.assunto && <Typography variant="body2"><strong>Assunto:</strong> {lead.assunto}</Typography>}
                            {lead.mensagem && <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>{lead.mensagem}</Typography>}
                            {lead.extra && Object.keys(lead.extra).length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                {Object.entries(lead.extra).map(([k, v]) => (
                                  <Typography key={k} variant="caption" display="block" color="text.secondary">
                                    <strong>{k}:</strong> {String(v)}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                            {!lead.mensagem && !lead.assunto && (!lead.extra || Object.keys(lead.extra).length === 0) && (
                              <Typography variant="caption" color="text.secondary">Sem detalhes adicionais.</Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default LeadsSite;
